import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import { BallisticMovementEffect } from "../effects/ballisticMovementEffect";
import { ParticleEffect } from "../effects/particleEffect";
import type { ParticleEmitter, PostDeserializable } from "../interfaces";
import { BasicAI } from "../ai/basicAI";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { PhysicalThing } from "../physicalThing";
import { randomChance, randomElement } from "../random";
import { deserializeXY, serializeXY } from "../serialization";
import { ArtilleryAimIndicator } from "../things/artilleryAimIndicator";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { XY } from "../xy";
import { RobotCorpse } from "./robotCorpse";

export class JetpackMob extends Mob implements ParticleEmitter, PostDeserializable {
	static key = "JetpackMob";

	override getBaseGoldValue(): number {
		return getMobGoldValue(JetpackMob.key);
	}

	private static readonly JUMP_RANGE_MIN = 4;
	private static readonly JUMP_RANGE_MAX = 60;
	private static readonly MIN_DEST_TO_PLAYER = 3;
	private static readonly MAX_DEST_TO_PLAYER = 5;
	private static readonly MIN_PLAYER_DIST_TO_JUMP = 5;
	private static readonly MAX_PLAYER_DIST_TO_JUMP = 25;
	private static readonly JUMP_PROBABILITY = 0.6;
	private static readonly JUMP_AIR_TIME_MS_PER_CELL = 200;
	private static readonly JUMP_HEIGHT = 2;
	private static readonly LIFTOFF_DUST_COUNT = 10;
	private static readonly LANDING_DUST_COUNT = 12;
	private static readonly TELEGRAPH_FLAME_INTENSITY = 0.25;

	private isJumping = false;
	private pendingJumpTarget: XY | null = null;
	private pendingJumpIndicator: ArtilleryAimIndicator | null = null;
	private ai: BasicAI;

	constructor(xy: XY) {
		super(xy);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.ai = new BasicAI(this);
		this.ai.attackSoundSupplier = () => new Sound(SoundId.MOB_PHISICAL_ATTACK, this.xy);
	}

	override getAnimationKey(): string {
		return "Robot";
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	override getParticleType(): ParticleType | null {
		if (this.isJumping || this.pendingJumpTarget !== null) return ParticleType.JETPACK_FLAME;
		return super.getParticleType();
	}

	override getParticleIntensity(): number {
		// Mob.getParticleIntensity scales with damage (1 - hp/maxHp), which is 0 at full HP and would
		// gate the emission interval to Infinity — no flame particles. Force a fixed intensity when
		// the mob is jumping or telegraphing so the jetpack trail emits regardless of the mob's HP.
		if (this.isJumping) return 1.0;
		if (this.pendingJumpTarget !== null) return JetpackMob.TELEGRAPH_FLAME_INTENSITY;
		return super.getParticleIntensity();
	}

	generateCorpse(): PhysicalThing | null {
		return new RobotCorpse(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.ROBOT_DIE);
	}

	processMobAction(board: Board): void {
		// Clear airborne flag from previous turn — we've now landed. Burst landing dust.
		if (this.isJumping) {
			this.isJumping = false;
			board.addEffect(new ParticleEffect(this.xy, ParticleType.STEP_DUST, JetpackMob.LANDING_DUST_COUNT));
		}

		if (this.pendingJumpTarget !== null) {
			const target = this.pendingJumpTarget;
			if (this.pendingJumpIndicator !== null) {
				board.removeAtEndOfTurn(this.pendingJumpIndicator);
			}
			this.pendingJumpTarget = null;
			this.pendingJumpIndicator = null;

			if (board.isInBounds(target) && board.isPassable(target) && !board.hasMob(target)) {
				this.isJumping = true;
				const oldXY = this.xy;
				this.xy = target;
				board.addEffect(new ParticleEffect(oldXY, ParticleType.STEP_DUST, JetpackMob.LIFTOFF_DUST_COUNT));
				const airTime = oldXY.distanceTo(target) * JetpackMob.JUMP_AIR_TIME_MS_PER_CELL;
				const effect = new BallisticMovementEffect(this, oldXY, this.xy, airTime);
				effect.height = JetpackMob.JUMP_HEIGHT;
				board.addEffect(effect);
				return;
			}
			// Destination invalid — fall through to normal AI.
		}

		const player = board.player;
		if (player) {
			const distanceMap = board.getDistanceMapToPlayer();
			const distance = distanceMap?.get(this.xy);
			if (
				distance !== undefined &&
				distance >= JetpackMob.MIN_PLAYER_DIST_TO_JUMP &&
				distance <= JetpackMob.MAX_PLAYER_DIST_TO_JUMP &&
				randomChance(JetpackMob.JUMP_PROBABILITY)
			) {
				const destination = this.pickJumpDestination(board);
				if (destination !== null) {
					const indicator = new ArtilleryAimIndicator(destination);
					board.addThing(indicator);
					this.pendingJumpIndicator = indicator;
					this.pendingJumpTarget = destination;
					return;
				}
			}
		}

		this.ai.processTurn();
	}

	private pickJumpDestination(board: Board): XY | null {
		const player = board.player;
		if (!player) return null;

		const currentDistToPlayer = this.xy.distanceTo(player.xy);

		const candidates: XY[] = [];
		for (let dy = -JetpackMob.JUMP_RANGE_MAX; dy <= JetpackMob.JUMP_RANGE_MAX; dy++) {
			for (let dx = -JetpackMob.JUMP_RANGE_MAX; dx <= JetpackMob.JUMP_RANGE_MAX; dx++) {
				const cell = new XY(this.xy.x + dx, this.xy.y + dy);
				if (!board.isInBounds(cell)) continue;
				const distFromMob = this.xy.distanceTo(cell);
				if (distFromMob < JetpackMob.JUMP_RANGE_MIN || distFromMob > JetpackMob.JUMP_RANGE_MAX) continue;
				if (!board.isPassable(cell)) continue;
				if (board.hasMob(cell)) continue;
				const distFromPlayer = player.xy.distanceTo(cell);
				if (distFromPlayer < JetpackMob.MIN_DEST_TO_PLAYER) continue;
				if (distFromPlayer > JetpackMob.MAX_DEST_TO_PLAYER) continue;
				if (distFromPlayer >= currentDistToPlayer) continue;
				candidates.push(cell);
			}
		}

		if (candidates.length === 0) return null;

		// Prefer candidates that are NOT on a straight line (cardinal or diagonal) from the player.
		const offLine = candidates.filter((c) => {
			const ddx = c.x - player.xy.x;
			const ddy = c.y - player.xy.y;
			return !(ddx === 0 || ddy === 0 || Math.abs(ddx) === Math.abs(ddy));
		});

		const pool = offLine.length > 0 ? offLine : candidates;
		return randomElement(pool);
	}

	serialize(): Record<string, unknown> {
		const data: Record<string, unknown> = { ...super.serialize() };
		if (this.pendingJumpTarget !== null) {
			data.pendingJumpTarget = serializeXY(this.pendingJumpTarget);
		}
		return data;
	}

	static deserialize(data: any): JetpackMob {
		const m = new JetpackMob(deserializeXY(data.xy));
		m.restoreMobFields(data);
		if (data.pendingJumpTarget) {
			m.pendingJumpTarget = deserializeXY(data.pendingJumpTarget);
		}
		return m;
	}

	postDeserialize(board: Board): void {
		// Recreate the aim indicator if we had a pending jump target when saved.
		if (this.pendingJumpTarget !== null) {
			const indicator = new ArtilleryAimIndicator(this.pendingJumpTarget);
			board.addThing(indicator);
			this.pendingJumpIndicator = indicator;
		}
	}
}
